import { createAsyncThunk } from "@reduxjs/toolkit";
import { Axios } from "api/axios";
import { API_ENDPOINT } from "helpers/Endpoints/endpoints";
import { FilterBuilder } from "helpers/ThunkHelper/FilterBuilder";

export const fetchCategories = createAsyncThunk(
  "category/fetchCategories",
  async ({
    page,
    pageSize,
    sort,
    order,
    search,
    filters,
  }: {
    page: number;
    pageSize: number;
    search?: string;
    sort?: string;
    order?: "asc" | "desc" | "";
    filters?: any[];
  }) => {
    try {
      const params = new URLSearchParams();
      params.append("page", page.toString());
      params.append("page_size", pageSize.toString());
      if (search) params.append("search", search.trim());
      if (sort) {
        const orderingValue = order === "desc" ? `-${sort}` : sort;
        params.append("ordering", orderingValue);
      }

      const filterPayload = FilterBuilder(filters ?? []);

      const body = {
        filters: filterPayload,
      };

      const response: any = await Axios.post(
        `${API_ENDPOINT?.MASTERS?.CATEGORY?.LIST}?${params.toString()}`,
        body,
      );
      return {
        data: response?.data?.data || [],
        rowCount: response.data.count || 0,
      };
    } catch (error: any) {
      throw new Error("Failed to fetch category");
    }
  },
);

export const exportCategoryCSV = async ({
  search,
  filters,
}: {
  search?: string;
  filters?: {
    filterField: string;
    filterValue: any;
  }[];
}) => {
  const params = new URLSearchParams();

  if (search) params.append("search", search);

  // -----------------------------
  // Build structured filters body
  // -----------------------------
  const filterPayload = FilterBuilder(filters ?? []);

  const body = {
    filters: filterPayload,
  };

  const response = await Axios.post(
    `${API_ENDPOINT.MASTERS.CATEGORY.EXPORT}?${params.toString()}`,
    body,
    { responseType: "blob" },
  );

  return {
    blob: response.data,
    filename:
      response.headers["content-disposition"]
        ?.split("filename=")[1]
        ?.replace(/"/g, "") ||
      `categories_${new Date().toISOString().split("T")[0]}.csv`,
  };
};

export const fetchCategoryById = createAsyncThunk(
  "category/fetchById",
  async (id: string, { rejectWithValue }) => {
    try {
      const response: any = await Axios.get(
        `${API_ENDPOINT.MASTERS.CATEGORY.DETAIL(id)}`,
      );
      return response.data.data;
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data || "Failed to fetch category by ID",
      );
    }
  },
);

export const fetchCategoryByDropDown = createAsyncThunk(
  "Category/fetchByDropDown",
  async (_, { rejectWithValue }) => {
    try {
      const response = await Axios.get(
        `${API_ENDPOINT.MASTERS.CATEGORY.DROPDOWN}`,
      );
      return response?.data?.data || [];
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data || "Failed to fetch Category list",
      );
    }
  },
);

export const updateCategory = createAsyncThunk(
  "category/updateCategory",
  async (data: any, { rejectWithValue }) => {
    try {
      const response: any = await Axios.put(
        `${API_ENDPOINT.MASTERS.CATEGORY.UPDATE(data.id)}`,
        data,
      );
      return response;
    } catch (err: any) {
      return rejectWithValue(err.response?.data || "Failed to update category");
    }
  },
);

export const updateStatusInCategory = createAsyncThunk(
  "category/updateStatusInCategory",
  async (id: string, { rejectWithValue }) => {
    try {
      const response: any = await Axios.patch(
        `${API_ENDPOINT.MASTERS.CATEGORY.STATUS_UPDATE(id)}`,
      );
      return response.data.data;
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data || "Failed to update status in category",
      );
    }
  },
);

export const addCategory = createAsyncThunk(
  "category/addCategory",
  async (data: any, { rejectWithValue }) => {
    try {
      const response: any = await Axios.post(
        `${API_ENDPOINT.MASTERS.CATEGORY.CREATE}`,
        data,
      );
      return response;
    } catch (err: any) {
      return rejectWithValue(err.response?.data || "Failed to add category");
    }
  },
);
